import moment from "moment";
import {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Link, useNavigate, useParams} from "react-router-dom";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import MainActions from "@/components/MainActions";
import Breadcrumbs from "@/components/Breadcrumbs";
import Tabs from "@/components/Tabs";
import PlayerHero from "@/components/PlayerHero";
import VLFCard from "@/components/Card";
import EmptyData from "@/components/EmptyData";
import ChoiceChips from "@/components/ChoiceChips";
import StatusBadge from "@/components/StatusBadge";
import ViewField from "@/components/Input/ViewField";
import VLFDatePicker from "@/components/Input/DatePicker";
import CardsTable from "@/components/CardsTable";
import ChartContainer from "@/components/ChartContainer";
import PlayerStatsContainer from "@/components/PlayerStatsContainer";
import PlayerRadarChart from "@/components/PlayerRadarChart";
import SmartRadarChart from "@/components/SmartRadarChart";
import {apiFieldsToValues} from "@/config/smartValuationConfig";
import Button from "@mui/material/Button";
import {Checkbox, FormControlLabel} from "@mui/material";
import VLFModal from "@/components/Modal";
import HandleCareerModal from "@/pages/Scouting/Players/Player/handleCareer";
import AttachmentsContainer from "@/components/AttachmentsContainer";
import HandlePhysicHeightInfo from "@/pages/Player/Modals/handlePhysicHeightInfo";
import HandlePhysicWeightInfo from "@/pages/Player/Modals/handlePhysicWeightInfo";
import HandleContactModal from "@/pages/Player/Modals/handleContact";
import HandleCertificateModal from "@/pages/Player/Modals/handleCertificate";
import HandleAgentModal from "@/pages/Player/Modals/handleAgent";
import ValuationsPage from "@/pages/Valuations";
import MedicalRecordPage from "@/pages/Medical/Record";
import MatchReportsPage from "@/pages/MatchReports";
import PlayerImportedGPS from "@/components/PlayerImportedGPS";
import PlayerTrainingStatistics from "@/components/PlayerTrainingStatistics";
import PlayerMatchStatistics from "@/components/PlayerMatchStatistics";
import PlayerDevelopmentProfile from "@/components/PlayerDevelopmentProfile";
import PlayerIndices from "@/components/PlayerIndices";
import TransferPlayerModal from "@/components/TransferPlayerModal";

import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ShareIcon from "@mui/icons-material/Share";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";

import {AttachmentAPI} from "@/api/attachment";
import {ContractAPI} from "@/api/contract";
import {PlayerAPI} from "@/api/player";
import {RosterAPI} from "@/api/roster";
import {PlayerValuationAPI} from "@/api/player_valuation";
import {MatchReportAPI} from "@/api/match_report";
import {MedicalAPI, isInjuryActiveAt} from "@/api/medical";
import {StatAPI} from "@/api/stat";

import {setPlayerTab} from "@/actions";

import {
	dateFormat,
	playerSemaphoreOptions,
	statusOptions,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";
import {getAttachment, isCompetitiveActivity} from "@/utils/functions";
import {generatePlayerReportPDF} from "@/utils/generatePlayerReportPDF";
import {loadPlayerRealStats} from "@/utils/computePlayerStats";
import {useCategories} from "@/utils/hooks";
import {AREAS, canRead, canWrite} from "@/utils/access";
import {
	CERTIFICATE_STATUS,
	certificateNote,
	certificateStatus,
} from "@/utils/certificates";

import "./index.scss";
import "../Manager/Match/index.scss";
import {useTranslation} from "react-i18next";
import {COLORS, SERIES} from "@/style/utils";

// Scadenza del contratto come tono della pillola nella testata
const CONTRACT_TONES = {
	safe: "good",
	warning: "warning",
	critical: "critical",
	expired: "muted",
	"no-contract": "muted",
};

function PlayerPage(props) {
	const {handleNotification} = props;

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const {id} = useParams();
	const {t} = useTranslation();

	const seasonId = useSelector((state) => state?.season?.id);
	const user = useSelector((state) => state?.user);
	const currentTab = useSelector((state) => state.playerTab);

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
	});

	const [loading, setLoading] = useState(false);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [openTransfer, setOpenTransfer] = useState(false);
	const [openDemote, setOpenDemote] = useState(false);
	const [playerData, setPlayerData] = useState({});

	const [openCareerDetail, setOpenCareerDetail] = useState(false);
	const [openContact, setOpenContact] = useState(false);
	const [openCertificate, setOpenCertificate] = useState(false);
	const [openAgents, setOpenAgents] = useState(false);
	const [openHandleAttachmentConfirm, setOpenHandleAttachmentConfirm] =
		useState(false);
	const [openPhysicInfoWeight, setOpenPhysicInfoWeight] = useState(false);
	const [openPhysicHeightInfo, setOpenPhysicHeightInfo] = useState(false);

	const [initialData, setInitialData] = useState();
	const [idAttachment, setIdAttachment] = useState();
	const [attachments, setAttachments] = useState([]);

	const [valuations, setValuations] = useState([]);
	const [valuationsData, setValuationsData] = useState([]);
	const [selectedValuationIds, setSelectedValuationIds] = useState([]);
	const [playerContracts, setPlayerContracts] = useState([]);
	const [playerMovements, setPlayerMovements] = useState([]);
	const [matchReports, setMatchReports] = useState([]);
	const [valSection, setValSection] = useState("valuations");
	const [isInjured, setIsInjured] = useState(false);
	const [coortePlayers, setCoortePlayers] = useState([]);

	// ------------------------
	const getValuations = async () => {
		try {
			const response = await PlayerValuationAPI.playerList(id, {seasonId});
			setValuations(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const getMatchReports = async () => {
		try {
			const response = await MatchReportAPI.list({playerId: id, seasonId});
			setMatchReports(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const getPlayerMovements = async () => {
		try {
			let response = (await PlayerAPI.getPlayerMovements(id)) || [];
			response = response.map((movement, index) => {
				return checkMovement(response, movement, index);
			});
			setPlayerMovements(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const getPlayerContracts = async () => {
		try {
			const response = await ContractAPI.listByPlayer(id);
			setPlayerContracts(response);
		} catch (error) {
			// non bloccante
		}
	};

	const getPlayerAttachments = async () => {
		try {
			const response = await AttachmentAPI.list("player", id);
			setAttachments(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const handleDownload = async (id) => {
		setLoading(true);
		try {
			await getAttachment(id, attachments);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteAttachment = async () => {
		setLoading(true);
		try {
			await AttachmentAPI.delete(idAttachment);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenHandleAttachmentConfirm(false);
			getPlayerAttachments();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	// ------------------------

	const checkMovement = (playerMovements, m, i) => {
		if (!m.club) {
			for (let i2 = i - 1; i2 >= 0; i2--) {
				if (playerMovements[i2]?.club) {
					m.club = playerMovements[i2]?.club;
					break;
				}
			}
		}

		if (!m.membershipDate) {
			for (let i2 = i - 1; i2 >= 0; i2--) {
				if (playerMovements[i2]?.membershipDate) {
					m.membershipDate = playerMovements[i2]?.membershipDate;
					break;
				}
			}
		}

		if (!m.movementType?.status) {
			for (let i2 = i - 1; i2 >= -1; i2--) {
				if (playerMovements[i2]?.movementType?.status) {
					m.movementType.status = playerMovements[i2]?.movementType?.status;
					break;
				}
			}
		}

		return m;
	};

	const getPlayer = async () => {
		setLoading(true);
		try {
			const response = await PlayerAPI.getById(id, {seasonId});
			response.hasStats = isCompetitiveActivity(
				categories,
				response.playerCategoryName,
			);

			response.career = response.career?.map((c) => {
				if (c.id?.startsWith("00000000")) {
					c.readable = true;
				}
				return c;
			});

			// controllo eventuali righe doppie per stagione-squadra-categoria
			const seen = new Set();
			response.career = response.career.filter((obj) => {
				const key = `${obj.season?.name}-${obj.club?.name}-${obj.playerCategory?.name}`;
				if (seen.has(key)) {
					return false;
				}
				seen.add(key);
				return true;
			});
			setPlayerData(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const getMedicalStatus = async () => {
		try {
			const list = await MedicalAPI.recordsList({playerId: id});
			const recordId = (list || []).find((r) => r.playerId === id)?.id;
			if (!recordId) return;
			const record = await MedicalAPI.recordGetById(recordId);
			setIsInjured(
				(record?.injuriesIllness ?? []).some((inj) =>
					isInjuryActiveAt(inj, moment()),
				),
			);
		} catch {
			// silently ignore — non-critical
		}
	};

	useEffect(() => {
		getPlayer();
		getPlayerContracts();
		getPlayerMovements();
		getMedicalStatus();
	}, [id, seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

	// Coorte per il benchmark del profilo di sviluppo: scaricata solo quando serve
	// (tab athletic_data), non a ogni apertura scheda.
	useEffect(() => {
		if (currentTab !== "athletic_data" || !seasonId) return;
		if (coortePlayers.length) return;
		PlayerAPI.list({prospect: false, seasonId})
			.then((res) => {
				setCoortePlayers((res || []).filter((p) => p.status === "Interno"));
			})
			.catch(() => {});
	}, [currentTab, seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

	// Aggiorna hasStats quando le categorie sono disponibili (caricamento asincrono)
	useEffect(() => {
		if (categories?.length && playerData) {
			setPlayerData((p) => ({
				...p,
				hasStats: isCompetitiveActivity(categories, p.playerCategoryName),
			}));
		}
	}, [categories]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (valuations?.length) {
			const promises = valuations.map((v) => {
				return PlayerValuationAPI.getById(v.id);
			});

			Promise.all(promises).then((responses) => {
				setValuationsData(responses);
			});
		}
	}, [valuations]);

	// Valutazioni ordinate dalla più recente (per il selettore del radar)
	const sortedValuations = useMemo(
		() =>
			[...(valuationsData || [])].sort(
				(a, b) => new Date(b.date) - new Date(a.date),
			),
		[valuationsData],
	);

	// Default: l'ultima valutazione per ciascun tipo (classica + SMART)
	const latestValuationIds = useMemo(() => {
		const latestClassic = sortedValuations.find((v) => v.valuationType !== 7);
		const latestSmart = sortedValuations.find((v) => v.valuationType === 7);
		return [latestClassic?.id, latestSmart?.id].filter(Boolean);
	}, [sortedValuations]);

	useEffect(() => {
		setSelectedValuationIds(latestValuationIds);
	}, [latestValuationIds]);

	// Solo le valutazioni selezionate alimentano i radar
	const selectedValuations = useMemo(
		() =>
			(valuationsData || []).filter((v) => selectedValuationIds.includes(v.id)),
		[valuationsData, selectedValuationIds],
	);

	const toggleValuation = (valuationId) =>
		setSelectedValuationIds((prev) =>
			prev.includes(valuationId)
				? prev.filter((x) => x !== valuationId)
				: [...prev, valuationId],
		);

	const deletePlayer = async () => {
		setLoading(true);
		try {
			await PlayerAPI.delete(playerData?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/players");
		} catch (error) {
			// Il BE risponde 409 (con messaggi su cosa lo blocca) se il giocatore
			// ha storico collegato (contratti, report, cartelle mediche, movimenti…).
			const data = error?.response?.data;
			let serverMessage = null;
			if (typeof data === "string") serverMessage = data;
			else if (Array.isArray(data)) serverMessage = data.join(" · ");
			else if (data)
				serverMessage =
					data.error ||
					data.message ||
					(Array.isArray(data.messages) ? data.messages.join(" · ") : null);

			if (error?.response?.status === 409) {
				handleNotification(
					serverMessage ||
						"Impossibile eliminare: il giocatore ha dati collegati. La cancellazione definitiva è consentita solo per schede senza storico.",
					"error",
				);
			} else {
				handleNotification(serverMessage || ERROR_OPERATION, "error");
			}
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	// Demote: rimuove il giocatore dalla rosa corrente senza rimetterlo nel pool
	// (returnToPool:false) → torna "esterno" (roster:null).
	const demoteFromInternal = async () => {
		if (!playerData?.rosterId) return;
		setLoading(true);
		try {
			await RosterAPI.deletePlayer(playerData.rosterId, playerData.id, false);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/players");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenDemote(false);
		}
	};

	const teamConfiguration = [];

	if (playerData?.goalkeeper) {
		teamConfiguration.push({
			value: playerData?.favoriteHand,
			label: t("favorite_hand"),
			size: "col-6 col-lg-4",
		});
	} else {
		teamConfiguration.push({
			value: playerData?.primaryRole,
			label: t("role"),
			size: "col-6 col-lg-4",
		});
		if (playerData?.secondaryRole) {
			teamConfiguration.push({
				value: playerData.secondaryRole,
				label: "Ruolo specifico",
				size: "col-6 col-lg-4",
			});
		}
	}

	teamConfiguration.push({
		value: playerData?.favoriteFoot,
		label: t("favorite_foot"),
		size: "col-6 col-lg-4",
	});

	if (user.roleName === "Admin") {
		teamConfiguration.push({
			value: playerData?.semaphore
				? playerSemaphoreOptions?.filter(
						(o) => o.value === playerData.semaphore,
					)?.[0]?.label
				: null,
			label: t("zone"),
			size: "col-6 col-lg-4",
		});
	}

	const appearanceConfiguration = [
		{
			value: playerData?.lastWeight
				? Number(playerData?.lastWeight?.toFixed(1)) + " kg"
				: "—",
			label: "Peso",
			size: "col-6 col-lg-4",
		},
		{
			value: Number(playerData?.weightHeightRatio?.toFixed(1)),
			label: "Peso/altezza",
			size: "col-6 col-lg-4",
		},
		{
			value: Number(playerData?.bmi?.toFixed(1)),
			label: "BMI",
			size: "col-6 col-lg-4",
		},

		{
			value: playerData?.lastHeight
				? Number(playerData?.lastHeight?.toFixed(1)) + " cm"
				: "—",
			label: "Altezza",
			size: "col-6 col-lg-4",
		},

		{
			value: Number(playerData?.heightWeightRatio?.toFixed(1)),
			label: "Altezza/peso",
			size: "col-6 col-lg-4",
		},
	];

	const statusLabel = useMemo(() => {
		if (!playerData?.status) {
			return playerData?.isInternal === true ? "Interno" : null;
		}

		return (
			statusOptions?.find((so) => so.value === playerData?.status)?.label ||
			playerData?.status
		);
	}, [playerData]);

	const zoneLabel = useMemo(() => {
		if (!playerData?.semaphore) {
			return null;
		}

		return (
			playerSemaphoreOptions?.find((o) => o.value === playerData?.semaphore)
				?.label || playerData?.semaphore
		);
	}, [playerData]);

	const playerRating = useMemo(() => {
		if (!valuationsData || !valuationsData.length) {
			return null;
		}

		let sum = 0;
		let count = 0;

		valuationsData.forEach((valuation) => {
			if (
				valuation.valuationRating !== null &&
				valuation.valuationRating !== undefined
			) {
				const value =
					typeof valuation.valuationRating === "number"
						? valuation.valuationRating
						: parseFloat(valuation.valuationRating);

				if (!Number.isNaN(value)) {
					sum += value;
					count += 1;
				}
			}
		});

		if (!count) {
			return null;
		}

		return sum / count;
	}, [valuationsData]);

	const matchReportsStats = useMemo(() => {
		if (!matchReports || !matchReports.length) {
			return {average: null, count: 0, trend: []};
		}

		let sum = 0;
		let count = 0;
		const trend = matchReports
			.map((report) => {
				const value =
					report?.generalRating !== null && report?.generalRating !== undefined
						? parseFloat(report.generalRating)
						: null;
				if (value !== null && !Number.isNaN(value)) {
					sum += value;
					count += 1;
				}
				return {
					date: report?.reportDate,
					rating: value !== null && !Number.isNaN(value) ? value : null,
				};
			})
			.filter((item) => item.rating !== null && item.date)
			.sort((a, b) => new Date(a.date) - new Date(b.date));

		return {
			average: count ? sum / count : null,
			count,
			trend,
		};
	}, [matchReports]);

	const heroHighlights = useMemo(() => {
		if (!playerData || !Object.keys(playerData).length) {
			return [];
		}

		const highlights = [];

		// ── Personal / DS-relevant ───────────────────────────────
		highlights.push(
			{
				id: "birthdate",
				label: t("birthdate"),
				value: playerData?.dateOfBirth
					? moment(playerData?.dateOfBirth).format(dateFormat)
					: null,
			},
			{
				id: "phone",
				label: t("phone"),
				value: playerData?.telephone,
			},
			{
				id: "email",
				label: "Email",
				value: playerData?.email,
			},
			{
				id: "birthplace",
				label: t("place_of_birth", {defaultValue: "Luogo di nascita"}),
				value: playerData?.placeOfBirth,
			},
			{
				id: "address",
				label: t("address"),
				value: playerData?.address,
			},
		);

		if (zoneLabel && user?.roleName === "Admin") {
			highlights.push({
				id: "zone-highlight",
				label: t("zone"),
				value: zoneLabel,
			});
		}

		return highlights.filter((item) => item.value);
	}, [playerData, zoneLabel, t, user?.roleName]);

	// Certificato medico-sportivo: lo gestiscono segreteria e area medica
	const showCertificates =
		canRead(user, AREAS.SEGRETERIA) || canRead(user, AREAS.AREA_MEDICA);
	const editCertificates =
		canWrite(user, AREAS.SEGRETERIA) || canWrite(user, AREAS.AREA_MEDICA);
	const certificate = certificateStatus(playerData?.certificates);
	const certificatesColumns = [
		{label: "Tipo", key: "name", align: "start"},
		{
			label: "Scadenza",
			key: "expiryDate",
			customRender: (item) =>
				item.expiryDate ? moment(item.expiryDate).format("DD/MM/YYYY") : "—",
		},
		{
			label: "Stato",
			key: "status",
			customRender: (item) => {
				const res = certificateStatus([item]);
				return (
					<StatusBadge
						tone={CERTIFICATE_STATUS[res.status].tone}
						label={CERTIFICATE_STATUS[res.status].label}
					/>
				);
			},
		},
	];

	const physicalHeightColumns = [
		{
			label: t("date_label"),
			key: "date",
			customRender: (item) => {
				return moment(item.date).format(dateFormat);
			},
		},
		{
			label: "Altezza",
			key: "height",
			customRender: (item) => {
				return item.height + " cm";
			},
		},
	];
	const physicalWeightColumns = [
		{
			label: t("date_label"),
			key: "date",
			customRender: (item) => {
				return moment(item.date).format(dateFormat);
			},
		},
		{
			label: "Peso",
			key: "weight",
			customRender: (item) => {
				return item.weight + " kg";
			},
		},
	];
	const contactsColumns = [
		{label: t("name"), key: "name", align: "start", medium: true},
		{label: "Email", key: "email", align: "start"},
		{label: "Telefono", key: "telephone", align: "start", small: true},
		{label: "Parentela", key: "relation", align: "start", small: true},
	];
	const agentsColumns = [
		{
			label: t("from"),
			key: "dateFrom",
			align: "center",
			medium: true,
			customRender: (item) => moment(item.dateFrom).format(dateFormat),
		},
		{label: t("name"), key: "name", align: "start"},
		{label: "Note", key: "notes", align: "start"},
	];
	const careerColumns = [
		{
			label: t("season"),
			key: "season",
			medium: true,
			customRender: (item) => item.season?.name,
		},
		{
			label: t("team"),
			key: "club",
			align: "start",
			customRender: (item) => item.club?.name,
		},
		{
			label: t("category"),
			key: "playerCategory",
			align: "start",
			customRender: (item) => item.playerCategory?.name,
		},
	];
	const contractTypeLabels = {
		professional: "Professionistico",
		amateur: "Dilettantistico",
		youth: "Settore giovanile",
		loan: "Prestito",
		amateur_professional: "Semiprofessionistico",
	};
	const contractColumns = [
		{
			label: "Tipo",
			key: "contractType",
			align: "start",
			small: true,
			customRender: (item) =>
				contractTypeLabels[item.contractType] || item.contractType,
		},
		{
			label: "Stipula",
			key: "signingDate",
			align: "center",
			small: true,
			customRender: (item) =>
				item.signingDate ? moment(item.signingDate).format(dateFormat) : "—",
		},
		{
			label: "Inizio",
			key: "startDate",
			align: "center",
			small: true,
			customRender: (item) =>
				item.startDate ? moment(item.startDate).format(dateFormat) : "—",
		},
		{
			label: "Scadenza",
			key: "endDate",
			align: "center",
			small: true,
			customRender: (item) =>
				item.endDate ? moment(item.endDate).format(dateFormat) : "—",
		},
		{
			label: "Stipendio fisso",
			key: "fixedMonthlySalary",
			align: "end",
			small: true,
			customRender: (item) =>
				item.fixedMonthlySalary != null
					? "€ " +
						Number(item.fixedMonthlySalary).toLocaleString("it-IT", {
							minimumFractionDigits: 0,
							maximumFractionDigits: 0,
						})
					: "—",
		},
		{
			label: "Tot. mensile",
			key: "totalMonthlySalary",
			align: "end",
			small: true,
			customRender: (item) =>
				item.totalMonthlySalary != null
					? "€ " +
						Number(item.totalMonthlySalary).toLocaleString("it-IT", {
							minimumFractionDigits: 0,
							maximumFractionDigits: 0,
						})
					: "—",
		},
		{
			label: "Attivo",
			key: "isActive",
			align: "center",
			small: true,
			customRender: (item) => (
				<div className={"cell" + (item.isActive ? " on" : " off")} />
			),
		},
		{
			label: "Gg alla scadenza",
			key: "daysToExpiry",
			align: "center",
			small: true,
			customRender: (item) => {
				const d = item.daysToExpiry;
				if (d === null || d === undefined) return "—";
				if (d < 0) return <StatusBadge label="Scaduto" />;
				return (
					<StatusBadge
						tone={d <= 30 ? "critical" : d <= 90 ? "warning" : "good"}
						label={d + " gg"}
					/>
				);
			},
		},
	];
	const movementsColumns = [
		{
			label: t("season"),
			key: "movementDate",
			align: "center",
			small: true,
			customRender: (item) => {
				const currentYear = moment(item.movementDate).year();
				const currentMonth = moment(item.movementDate).month() + 1;

				return currentMonth <= 6
					? "" + (currentYear - 1).toString() + "/" + currentYear
					: "" + currentYear + "/" + (currentYear + 1);
			},
		},
		{
			label: "Società",
			key: "club?.name",
			align: "start",
			medium: true,
			customRender: (item) => item.club?.name,
		},
		{
			label: "Tesseramento",
			key: "membershipDate",
			align: "center",
			medium: true,
			customRender: (item) => moment(item.membershipDate).format(dateFormat),
		},
		{
			label: "Stato",
			key: "movementType?.status",
			align: "start",
			medium: true,
			customRender: (item) => item.movementType?.status,
		},
		{
			label: t("date_label"),
			key: "movementDate",
			align: "center",
			medium: true,
			customRender: (item) => moment(item.movementDate).format(dateFormat),
		},
		{
			label: "Tipo",
			key: "movementType",
			align: "start",
			customRender: (item) => item.movementType?.description,
		},
	];

	const handleClose = () => {
		setOpenPhysicInfoWeight(false);
		setOpenPhysicHeightInfo(false);
		setOpenContact(false);
		setOpenCertificate(false);
		setOpenAgents(false);
		setInitialData();
	};

	const handlePhysicInfo = async (data, id) => {
		setLoading(true);
		try {
			const body = {
				playerId: playerData?.id,
				...data,
				height: !data.height ? "0" : data.height,
				date: moment(data.date).format("YYYY-MM-DD"),
			};
			id
				? await PlayerAPI.updateGrowth(playerData?.id, id, body)
				: await PlayerAPI.createGrowth(body);
			handleClose();
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const deletePhysicInfo = async (id) => {
		setLoading(true);
		try {
			await PlayerAPI.deleteGrowth(playerData?.id, id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const handleContact = async (data, id) => {
		setLoading(true);
		try {
			const body = {
				playerId: playerData?.id,
				contactRole: data?.contactRole || "",
				...data,
			};
			id
				? await PlayerAPI.updateContact(playerData?.id, id, body)
				: await PlayerAPI.createContact(body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
			handleClose();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const handleCertificate = async (data, id) => {
		setLoading(true);
		try {
			const body = {
				name: data.name,
				expiryDate: moment(data.expiryDate).format("YYYY-MM-DD"),
				active: data.active ?? true,
			};
			id
				? await PlayerAPI.updateCertificate(playerData?.id, id, body)
				: await PlayerAPI.createCertificate(playerData?.id, body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
			handleClose();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const deleteCertificate = async (id) => {
		setLoading(true);
		try {
			await PlayerAPI.deleteCertificate(playerData?.id, id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const deleteContact = async (id) => {
		setLoading(true);
		try {
			await PlayerAPI.deleteContact(playerData?.id, id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const handleAgent = async (data, id) => {
		setLoading(true);
		try {
			let body = {
				playerId: playerData?.id,
				...data,
				dateFrom: moment(data?.dateFrom).format("YYYY-MM-DD"),
			};
			if (!id) {
				body = [body];
			}
			id
				? await PlayerAPI.updateAgent(playerData?.id, id, body)
				: await PlayerAPI.createAgent(body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
			handleClose();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const deleteAgent = async (id) => {
		setLoading(true);
		try {
			await PlayerAPI.deleteAgent(playerData?.id, id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const handleCareerDetail = async (data, id) => {
		setLoading(true);
		try {
			const body = {
				...data,
				playerId: playerData?.id,
				seasonId: data?.careerSeason?.value,
				clubId: data?.careerClub?.value,
				playerCategoryId: data?.careerCategory?.value,
			};

			id
				? await PlayerAPI.updateCareer(playerData?.id, id, body)
				: await PlayerAPI.createCareer(body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
			setOpenCareerDetail(false);
			setInitialData();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteCareerDetail = async (id) => {
		setLoading(true);
		try {
			await PlayerAPI.deleteCareer(playerData?.id, id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayer();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteContract = async (contractId) => {
		setLoading(true);
		try {
			await ContractAPI.delete(contractId);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getPlayerContracts();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const breadcrumbsElements = [
		{
			label: "Giocatori",
			path: "/players",
		},
		{label: "/"},
	];

	const handleExportReport = async () => {
		try {
			setLoading(true);
			const [medicalRes, contractsRes, realStats] = await Promise.all([
				MedicalAPI.recordsList({playerId: id}).catch(() => []),
				ContractAPI.listByPlayer(id).catch(() => []),
				loadPlayerRealStats(playerData, seasonId).catch(() => null),
			]);

			const allInjuries = (medicalRes || [])
				.filter((r) => Array.isArray(r.injuriesIllness))
				.flatMap((r) => r.injuriesIllness.filter((i) => !i.recordType));

			// Calcola media SMART values se ci sono valutazioni tipo 7
			const smartVals = (valuationsData || []).filter(
				(v) => v.valuationType === 7,
			);
			let smartAvgValues = null;
			if (smartVals.length) {
				smartAvgValues = {};
				const pids = [
					"decision_making",
					"concentration",
					"mentality",
					"team_play",
					"control",
					"passing",
					"dribbling",
					"finishing",
					"speed",
					"explosiveness",
					"stamina",
					"strength",
					"positioning",
					"game_reading",
					"off_ball_movement",
					"vision",
					"offensive_duels",
					"defensive_duels",
					"aerial_duels",
				];
				pids.forEach((pid) => {
					const vals = smartVals
						.map((v) => {
							const parsed = apiFieldsToValues(v);
							return parsed[pid];
						})
						.filter((x) => x != null);
					smartAvgValues[pid] = vals.length
						? vals.reduce((s, x) => s + x, 0) / vals.length
						: null;
				});
			}

			generatePlayerReportPDF({
				playerData,
				contracts: contractsRes,
				injuries: allInjuries,
				valuations: valuationsData,
				realStats,
				smartValues: smartAvgValues,
			});
			handleNotification("PDF generato con successo", "success");
		} catch (error) {
			handleNotification("Errore nella generazione del PDF", "error");
		} finally {
			setLoading(false);
		}
	};

	const handleSharePortfolio = () => {
		const url = window.location.origin + "/public/player/" + playerData?.id;
		navigator.clipboard
			.writeText(url)
			.then(() =>
				handleNotification("Link portfolio copiato negli appunti", "success"),
			)
			.catch(() => handleNotification("Impossibile copiare il link", "error"));
	};

	// Azioni della pagina, in tutte le schede: un solo pulsante pieno (Modifica)
	const pageActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate("/players"),
			color: "info",
		},
		{
			icon: <PictureAsPdfIcon />,
			title: "Report",
			variant: "outlined",
			onClick: handleExportReport,
		},
		{
			icon: <ShareIcon />,
			title: "Portfolio",
			variant: "outlined",
			onClick: handleSharePortfolio,
		},
		...(playerData?.rosterId
			? [
					{
						icon: <SwapHorizIcon />,
						title: "Sposta rosa",
						variant: "outlined",
						onClick: () => setOpenTransfer(true),
					},
					{
						icon: <PersonRemoveIcon />,
						title: "Rimuovi dagli interni",
						variant: "outlined",
						onClick: () => setOpenDemote(true),
						color: "warning",
					},
				]
			: []),
		{
			icon: <EditIcon />,
			title: "Modifica",
			onClick: () => navigate("/players/" + playerData?.id + "/edit"),
		},
		{
			icon: <DeleteIcon />,
			title: "Elimina",
			variant: "outlined",
			onClick: () => setOpenHandleConfirm(true),
			color: "error",
		},
	];

	const valuationActions =
		currentTab === "valuations" && playerData?.status !== "Esterno"
			? [
					{
						icon: <AddIcon />,
						title: "Aggiungi valutazione",
						variant: "outlined",
						onClick: () => navigate("/players-valuations/new?playerId=" + id),
					},
					{
						icon: <AutoAwesomeIcon />,
						title: "Valutazione SMART",
						variant: "outlined",
						onClick: () =>
							navigate("/players-valuations/smart/new?playerId=" + id),
					},
				]
			: [];

	const mainActions = [...pageActions, ...valuationActions];

	const getChartFilters = (filter, setFilter, date, setDate) => {
		return (
			<div className="col-sm-12">
				<div className="stat-options">
					<div className="stat-switch">
						<div
							className={
								"single-option clickable" +
								(filter === "seasonal" ? " selected" : "")
							}
							onClick={() => {
								setFilter("seasonal");
							}}
						>
							Stagionale
						</div>
						<div
							className={
								"single-option clickable" +
								(filter === "monthly" ? " selected" : "")
							}
							onClick={() => {
								setFilter("monthly");
							}}
						>
							Mensile
						</div>
					</div>
					{filter === "monthly" ? (
						<div className="stat-input mx-5">
							<VLFDatePicker
								type="month"
								customDateFormat="MM/yyyy"
								value={date}
								onChange={setDate}
								required
								size="small"
							/>
						</div>
					) : null}
				</div>
			</div>
		);
	};
	const getChartStats = (stats, fieldKey, filter) => {
		return (
			<div className="col-sm-12">
				<div className="stats-box-container mt-2">
					{stats.map((s, k) => {
						return (
							<div key={k} className="stat-box">
								<div className="stat-value">
									{Math.round(s[fieldKey] * 100) / 100}
								</div>
								<div className="stat-label">
									{filter === "monthly"
										? "Settimana " + (s.weekNumber || "")
										: s.year
											? moment()
													.set({
														month: s.month - 1,
														year: s.year,
													})
													.format("MMMM YYYY")
											: ""}
								</div>
							</div>
						);
					})}
				</div>
			</div>
		);
	};

	const [tqrStats, setTQRStats] = useState([]);
	const [rpeStats, setRPEStats] = useState([]);
	const [trainingsStats, setTrainingsStats] = useState([]);
	const [includePreparation, setIncludePreparation] = useState(false);
	const [statisticsView, setStatisticsView] = useState("matches");
	const [trainingsStatsLoading, setTrainingsStatsLoading] = useState(false);

	const [tqrFilter, setTQRFilter] = useState("seasonal");
	const [tqrDate, setTQRDate] = useState(new Date());
	const [rpeFilter, setRPEFilter] = useState("seasonal");
	const [rpeDate, setRPEDate] = useState(new Date());
	const [trainingsFilter, setTrainingsFilter] = useState("weekly");
	const [trainingsDate, setTrainingsDate] = useState(new Date());

	const getRPEStats = async () => {
		try {
			const response =
				rpeFilter === "monthly"
					? await StatAPI.weeklyRPE({
							groupMode: 2,
							playerId: id,
							month: moment(rpeDate).month() + 1,
							year: moment(rpeDate).year(),
						})
					: await StatAPI.monthlyRPE({groupMode: 1, playerId: id, seasonId});
			setRPEStats(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};
	const getTQRStats = async () => {
		try {
			const response =
				tqrFilter === "monthly"
					? await StatAPI.weeklyTQR({
							groupMode: 2,
							playerId: id,
							month: moment(tqrDate).month() + 1,
							year: moment(tqrDate).year(),
						})
					: await StatAPI.monthlyTQR({groupMode: 1, playerId: id, seasonId});
			setTQRStats(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	useEffect(() => {
		if (currentTab === "main_info") {
			getPlayerAttachments();
		}
	}, [currentTab]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (currentTab === "main_info" || currentTab === "valuations") {
			getValuations();
			if (playerData?.goalkeeper) {
				getMatchReports();
			}
		}
	}, [currentTab, playerData]);

	useEffect(() => {
		let active = true;
		const getTrainingsStats = async () => {
			setTrainingsStatsLoading(true);
			setTrainingsStats([]);
			try {
				const currentWeek = moment(trainingsDate).isoWeek();
				const response =
					trainingsFilter === "daily"
						? await StatAPI.trainingsDetails(id, {
								playerId: id,
								includePreparation,
								seasonId,
								yearWeek:
									moment(trainingsDate).isoWeekYear() +
									"" +
									(currentWeek < 10 ? "0" + currentWeek : currentWeek),
							})
						: await StatAPI.weeklyTrainings(id, {
								playerId: id,
								includePreparation,
								seasonId,
								month: moment(trainingsDate).month() + 1,
								year: moment(trainingsDate).year(),
								grouped: false,
							});
				if (!active) return;
				setTrainingsStats(
					(response || [])
						.filter(
							(r) =>
								moment(r.trainingDate || r.weekStartDate).isValid() &&
								moment(r.trainingDate || r.weekStartDate).year() < 2050,
						)
						.map((r) => {
							if (r.trainingData) {
								r.startDate = r.trainingData.startDate;
							}
							return r;
						}),
				);
			} catch (error) {
				if (active) handleNotification(ERROR_OPERATION, "error");
			} finally {
				if (active) setTrainingsStatsLoading(false);
			}
		};

		if (currentTab === "stats" && statisticsView === "trainings")
			getTrainingsStats();
		return () => {
			active = false;
		};
	}, [
		currentTab,
		statisticsView,
		trainingsFilter,
		trainingsDate,
		seasonId,
		id,
		includePreparation,
	]);

	// ---

	useEffect(() => {
		if (currentTab === "performance") {
			getRPEStats();
		}
	}, [currentTab, rpeFilter, rpeDate]);
	useEffect(() => {
		if (currentTab === "performance") {
			getTQRStats();
		}
	}, [currentTab, tqrFilter, tqrDate]);

	useEffect(() => {
		if (
			currentTab &&
			tabs?.filter((t) => t.key === currentTab)?.[0].hidden &&
			Object.keys(playerData).length > 0
		) {
			dispatch(setPlayerTab("main_info"));
		}
	}, [currentTab, playerData]);

	const tabs = [
		{
			key: "main_info",
			label: t("informations"),
			render: () => {
				return (
					<>
						<div className="player-page__hero-section">
							<VLFCard
								className="player-spotlight-card"
								openable={false}
								content={
									<div className="player-spotlight">
										<div className="player-spotlight__highlights">
											{heroHighlights.length ? (
												heroHighlights.map((item) => (
													<div
														key={item.id}
														className="player-spotlight__highlight"
													>
														<span className="player-spotlight__highlight-label">
															{item.label}
														</span>
														<span
															className={
																"player-spotlight__highlight-value" +
																(item.isEmphasized
																	? " player-spotlight__highlight-value--number"
																	: "")
															}
														>
															{item.value ?? "—"}
														</span>
													</div>
												))
											) : (
												<EmptyData />
											)}
										</div>
									</div>
								}
							/>
						</div>
						<div className="row no-margin mt-2">
							<div className="col-sm-12 padding-small">
								<VLFCard
									title={t("main_information")}
									content={
										<>
											<div className="row">
												<div className="col-12 col-xl-6">
													{sortedValuations.length > 0 && (
														<div className="player-valuation-picker">
															<span className="player-valuation-picker__label">
																Valutazioni
															</span>
															<button
																type="button"
																className="player-valuation-picker__link"
																onClick={() =>
																	setSelectedValuationIds(
																		sortedValuations.map((v) => v.id),
																	)
																}
															>
																Tutte
															</button>
															<button
																type="button"
																className="player-valuation-picker__link"
																onClick={() =>
																	setSelectedValuationIds(latestValuationIds)
																}
															>
																Ultima
															</button>
															{sortedValuations.map((v) => (
																<button
																	key={v.id}
																	type="button"
																	className={
																		"choice-chip" +
																		(selectedValuationIds.includes(v.id)
																			? " is-active"
																			: "")
																	}
																	onClick={() => toggleValuation(v.id)}
																>
																	{moment(v.date).format(dateFormat)}
																	{v.valuationType === 7 ? " · SMART" : ""}
																</button>
															))}
														</div>
													)}
													{sortedValuations.length > 0 ? (
														<PlayerRadarChart
															playerData={playerData}
															valuationsData={selectedValuations}
														/>
													) : (
														<div className="empty-data-container padding-small">
															Nessuna valutazione
														</div>
													)}
													{/* SMART radar (se ci sono valutazioni tipo 7) */}
													{(() => {
														const smartVals = (selectedValuations || []).filter(
															(v) => v.valuationType === 7,
														);
														if (!smartVals.length) return null;
														const avgValues = {};
														const smartParams = [
															"decision_making",
															"concentration",
															"mentality",
															"team_play",
															"control",
															"passing",
															"dribbling",
															"finishing",
															"speed",
															"explosiveness",
															"stamina",
															"strength",
															"positioning",
															"game_reading",
															"off_ball_movement",
															"vision",
															"offensive_duels",
															"defensive_duels",
															"aerial_duels",
														];
														smartParams.forEach((pid) => {
															const vals = smartVals
																.map((v) => apiFieldsToValues(v)[pid])
																.filter((x) => x != null);
															avgValues[pid] = vals.length
																? vals.reduce((s, x) => s + x, 0) / vals.length
																: null;
														});
														return (
															<div className="mt-3">
																<SmartRadarChart
																	values={avgValues}
																	playerName={`${playerData?.firstName} ${playerData?.lastName}`}
																	compact
																/>
															</div>
														);
													})()}
												</div>
												<div className="col-12 col-xl-6">
													<ViewField
														configuration={[
															...teamConfiguration,
															...appearanceConfiguration,
														]}
													/>
												</div>
											</div>
											<PlayerMatchStatistics
												key={id + seasonId}
												playerId={id}
												seasonId={seasonId}
												summaryOnly
											/>
										</>
									}
								/>
							</div>
						</div>
						<div className="row no-margin mt-2">
							<div className="col-sm-12 padding-small">
								<CardsTable
									title="Contatti"
									fullHeight={false}
									middleHeight
									configurationColumns={contactsColumns}
									data={playerData?.playersContacts}
									actions={[
										{
											icon: <AddIcon />,
											onClick: () => setOpenContact(true),
										},
									]}
									rowActions
									handleEdit={(item) => {
										setInitialData(Object.assign({}, item));
										setOpenContact(true);
									}}
									handleDelete={(item) => {
										deleteContact(item.id);
									}}
								/>
							</div>
						</div>
						{showCertificates && (
							<div className="row no-margin mt-2">
								<div className="col-sm-12 padding-small">
									<CardsTable
										title="Certificato medico-sportivo"
										fullHeight={false}
										middleHeight
										configurationColumns={certificatesColumns}
										data={playerData?.certificates}
										actions={
											editCertificates
												? [
														{
															icon: <AddIcon />,
															title: "Nuovo certificato",
															onClick: () => setOpenCertificate(true),
														},
													]
												: []
										}
										rowActions
										handleEdit={
											editCertificates
												? (item) => {
														setInitialData(Object.assign({}, item));
														setOpenCertificate(true);
													}
												: undefined
										}
										handleDelete={
											editCertificates
												? (item) => deleteCertificate(item.id)
												: undefined
										}
									/>
								</div>
							</div>
						)}
						<div className="row no-margin mt-2">
							<div className="col-sm-12 padding-small">
								<CardsTable
									title="Agenti"
									fullHeight={false}
									middleHeight
									configurationColumns={agentsColumns}
									data={playerData?.playersAgentsHistory}
									actions={[
										{
											icon: <AddIcon />,
											onClick: () => setOpenAgents(true),
										},
									]}
									rowActions
									handleEdit={(item) => {
										setInitialData(Object.assign({}, item));
										setOpenAgents(true);
									}}
									handleDelete={(item) => {
										deleteAgent(item.id);
									}}
								/>
							</div>
						</div>
						{playerMovements.length > 0 && (
							<div className="row no-margin mt-2">
								<div className="col-sm-12 padding-small">
									<CardsTable
										title="Movimenti"
										fullHeight={false}
										middleHeight
										configurationColumns={movementsColumns}
										data={playerMovements}
									/>
								</div>
							</div>
						)}
						<div className="row no-margin mt-3">
							<div className="col-sm-12 padding-small">
								<CardsTable
									title="Carriera"
									fullHeight={false}
									middleHeight
									configurationColumns={careerColumns}
									data={playerData?.career}
									actions={[
										{
											icon: <AddIcon />,
											onClick: () => setOpenCareerDetail(true),
										},
									]}
									handleEdit={(item) => {
										setInitialData(Object.assign({}, item));
										setOpenCareerDetail(true);
									}}
									handleDelete={(item) => {
										deleteCareerDetail(item.id);
									}}
									defaultSortedField="dateEvent"
									defaultSortedOrder="descend"
								/>
							</div>
						</div>
						<div className="row no-margin mt-3">
							<div className="col-sm-12 padding-small">
								<CardsTable
									title="Contratti"
									fullHeight={false}
									middleHeight
									configurationColumns={contractColumns}
									data={playerContracts}
									actions={[
										{
											icon: <AddIcon />,
											onClick: () =>
												navigate(
													"/manager/players-contracts/new?playerId=" + id,
												),
										},
									]}
									handleClick={(item) =>
										navigate("/manager/players-contracts/" + item.id)
									}
									handleEdit={(item) => {
										navigate("/manager/players-contracts/" + item.id + "/edit");
									}}
									handleDelete={(item) => {
										deleteContract(item.id);
									}}
									defaultSortedField="startDate"
									defaultSortedOrder="descend"
									rowClassName={(item) => {
										if (!item.isActive) return "";
										const d = item.daysToExpiry;
										if (d <= 30) return "warning";
										return "";
									}}
								/>
							</div>
						</div>
						{
							<div className="row no-margin mt-2">
								<div className="col-sm-12 padding-small mt-2">
									<VLFCard
										title={t("attachments")}
										content={
											<AttachmentsContainer
												entityId={id}
												entityType="player"
												attachments={attachments}
												handleAdd={() => getPlayerAttachments()}
												handleDownload={handleDownload}
												handleDelete={(id) => {
													setIdAttachment(id);
													setOpenHandleAttachmentConfirm(true);
												}}
												handleNotification={handleNotification}
											/>
										}
									/>
									<VLFModal
										open={openHandleAttachmentConfirm}
										title="Eliminazione"
										content={
											<div className="mt-2">
												Sei sicuro di voler eliminare questo elemento?
											</div>
										}
										confirm={() => deleteAttachment()}
										close={() => setOpenHandleAttachmentConfirm(false)}
										confirmLabel="Conferma"
									/>
								</div>
							</div>
						}
						{openContact ? (
							<HandleContactModal
								open={openContact}
								confirm={handleContact}
								close={handleClose}
								initialData={initialData}
							/>
						) : null}
						{openCertificate ? (
							<HandleCertificateModal
								open={openCertificate}
								confirm={handleCertificate}
								close={handleClose}
								initialData={initialData}
							/>
						) : null}
						{openAgents ? (
							<HandleAgentModal
								open={openAgents}
								confirm={handleAgent}
								close={handleClose}
								initialData={initialData}
							/>
						) : null}
						<VLFModal
							open={openHandleConfirm}
							title="Eliminazione"
							content={
								<div className="mt-2">
									Stai per eliminare definitivamente{" "}
									<b>
										{(playerData?.firstName || "") +
											" " +
											(playerData?.lastName || "")}
									</b>
									.
									<br />
									L'operazione è <b>irreversibile</b> ed è consentita solo se il
									giocatore non ha storico collegato (contratti, report,
									cartelle mediche, movimenti). In caso contrario verrà bloccata
									dal sistema.
								</div>
							}
							confirm={() => deletePlayer()}
							close={() => setOpenHandleConfirm(false)}
							confirmLabel="Elimina definitivamente"
						/>
						<TransferPlayerModal
							open={openTransfer}
							close={() => setOpenTransfer(false)}
							player={playerData}
							onSuccess={() => getPlayer()}
							handleNotification={handleNotification}
						/>
						<VLFModal
							open={openDemote}
							title="Rimuovi dagli interni"
							content={
								<div className="mt-2">
									Stai per rimuovere{" "}
									<b>
										{(playerData?.firstName || "") +
											" " +
											(playerData?.lastName || "")}
									</b>{" "}
									dagli interni: uscirà dalla rosa{" "}
									<b>{playerData?.roster?.name || ""}</b> e tornerà tra i
									giocatori esterni. Nessuno storico viene perso e potrai sempre
									riportarlo negli interni.
								</div>
							}
							confirm={() => demoteFromInternal()}
							close={() => setOpenDemote(false)}
							confirmLabel="Rimuovi dagli interni"
						/>
					</>
				);
			},
		},
		{
			key: "stats",
			label: t("stats"),
			render: () => {
				return (
					<div className="row no-margin col-container">
						<div className="col-12 padding-small">
							<PlayerIndices
								playerData={playerData}
								valuationsData={valuationsData}
								isInjured={isInjured}
							/>
						</div>
						<div className="col-12 padding-small player-statistics-tabs">
							<ChoiceChips
								options={[
									{value: "matches", label: "Partite"},
									{value: "trainings", label: "Allenamenti"},
								]}
								value={statisticsView}
								onChange={setStatisticsView}
							/>
						</div>
						<div
							className="col-12"
							role="tabpanel"
							id="player-statistics-matches"
							aria-labelledby="player-statistics-matches-tab"
							hidden={statisticsView !== "matches"}
						>
							<div className="col-12 padding-small">
								<PlayerMatchStatistics
									showTitle={false}
									key={id + seasonId}
									playerId={id}
									seasonId={seasonId}
								/>
							</div>
						</div>
						<div
							className="col-12"
							role="tabpanel"
							id="player-statistics-trainings"
							aria-labelledby="player-statistics-trainings-tab"
							hidden={statisticsView !== "trainings"}
						>
							<PlayerTrainingStatistics
								records={trainingsStats}
								loading={trainingsStatsLoading}
								filter={trainingsFilter}
								setFilter={setTrainingsFilter}
								date={trainingsDate}
								setDate={setTrainingsDate}
								includePreparation={includePreparation}
								setIncludePreparation={setIncludePreparation}
							/>
						</div>
					</div>
				);
			},
		},
		{
			key: "performance",
			label: "Performance",
			render: () => {
				return (
					<div className="row no-margin col-container player-performance">
						<div className="col-12 padding-small">
							<FormControlLabel
								control={
									<Checkbox
										size="small"
										checked={includePreparation}
										onChange={(event) =>
											setIncludePreparation(event.target.checked)
										}
									/>
								}
								label="Includi preparazione (+30′ per seduta)"
							/>
							<p className="performance-preparation-note">
								Il flag vale per gli allenamenti, come nella scheda Statistiche.
								Le durate salvate non cambiano.
							</p>
						</div>
						<div className="col-12 padding-small performance-current">
							<PlayerStatsContainer
								type="rpe"
								player={playerData}
								includePreparation={includePreparation}
							/>
							<PlayerStatsContainer type="tqr" player={playerData} />
						</div>
						{playerData?.hasStats ? (
							<div className="col-12 col-lg-6 padding-small performance-chart">
								<VLFCard
									title="Recupero · TQR"
									openable={false}
									content={
										<div className="row ">
											{getChartFilters(
												tqrFilter,
												setTQRFilter,
												tqrDate,
												setTQRDate,
											)}
											{getChartStats(tqrStats, "avgTqr", tqrFilter)}
											<div className="col-sm-12 mt-2">
												<ChartContainer
													type="line"
													chartItems={[
														{
															value: "minTqr",
															label: "Minimo",
															color: SERIES[0],
														},
														{
															value: "avgTqr",
															label: "Medio",
															color: SERIES[1],
														},
														{
															value: "maxTqr",
															label: "Massimo",
															color: SERIES[2],
														},
													]}
													data={tqrStats.map((ts) => {
														const date = moment().set({
															year: ts.year,
															month: ts.month - 1,
														});
														return {
															...ts,
															chartLabel:
																tqrFilter === "monthly"
																	? "Settimana " + ts.weekNumber
																	: date.format("MMMM YYYY"),
														};
													})}
													dataKey="chartLabel"
												/>
											</div>
										</div>
									}
								/>
							</div>
						) : null}
						{playerData?.hasStats ? (
							<div className="col-12 col-lg-6 padding-small performance-chart">
								<VLFCard
									title="Intensità · RPE"
									openable={false}
									content={
										<div className="row">
											{getChartFilters(
												rpeFilter,
												setRPEFilter,
												rpeDate,
												setRPEDate,
											)}
											{getChartStats(rpeStats, "avgRpe", rpeFilter)}
											<div className="col-sm-12 mt-2">
												<ChartContainer
													type="line"
													chartItems={[
														{
															value: "minRpe",
															label: "Minimo",
															color: SERIES[0],
														},
														{
															value: "avgRpe",
															label: "Medio",
															color: SERIES[1],
														},
														{
															value: "maxRpe",
															label: "Massimo",
															color: SERIES[2],
														},
													]}
													data={rpeStats.map((rs) => {
														const date = moment().set({
															year: rs.year,
															month: rs.month - 1,
														});
														return {
															...rs,
															chartLabel:
																rpeFilter === "monthly"
																	? "Settimana " + rs.weekNumber
																	: date.format("MMMM YYYY"),
														};
													})}
													dataKey="chartLabel"
												/>
											</div>
										</div>
									}
								/>
							</div>
						) : null}
						<div className="col-12 padding-small performance-tests">
							<header>
								<strong>Test atletici</strong>
								<Button
									size="small"
									aria-label="Aggiungi test atletico"
									onClick={() => navigate("/athletic-tests/new?playerId=" + id)}
								>
									<AddIcon />
								</Button>
							</header>
							<div className="performance-tests__table">
								<table>
									<thead>
										<tr>
											<th>Data</th>
											<th>Test e descrizione</th>
											<th>Misure</th>
											<th>Note</th>
										</tr>
									</thead>
									<tbody>
										{[...(playerData?.athleticTests || [])]
											.sort(
												(a, b) => new Date(b.testDate) - new Date(a.testDate),
											)
											.map((test) => (
												<tr key={test.testId || test.id}>
													<td>{moment(test.testDate).format("DD/MM/YYYY")}</td>
													<td>
														<Link
															to={"/athletic-tests/" + (test.testId || test.id)}
														>
															{test.testType}
														</Link>
														{test.description && (
															<small>{test.description}</small>
														)}
													</td>
													<td>
														<div className="performance-tests__values">
															{test.details?.map((detail, index) => (
																<span
																	key={detail.id || index}
																	title={
																		detail.description || detail.notes || ""
																	}
																>
																	{detail.valueType}{" "}
																	<strong>{detail.value ?? "—"}</strong>
																</span>
															))}
														</div>
													</td>
													<td className="performance-tests__notes">
														{test.notes || "—"}
													</td>
												</tr>
											))}
									</tbody>
								</table>
							</div>
							{!playerData?.athleticTests?.length && (
								<p>Nessun test atletico registrato.</p>
							)}
						</div>
						<div className="col-sm-12 padding-small">
							<div className="card-outside-title-container mt-3">
								<div className="card-outside-title">GPS</div>
							</div>
						</div>
						<div className="col-sm-12 padding-small">
							<PlayerImportedGPS playerId={id} seasonId={seasonId} />
						</div>
					</div>
				);
			},
		},
		{
			key: "athletic_data",
			label: t("anthropometric_data"),
			render: () => {
				return (
					<div>
						<div className="row no-margin col-container">
							<div className="col-12 padding-small">
								<PlayerDevelopmentProfile
									playerData={playerData}
									coortePlayers={coortePlayers}
								/>
							</div>
						</div>
						<div className="row no-margin col-container">
							<div className="col-12 col-lg-6 padding-small">
								<CardsTable
									title="Altezza"
									fullHeight={false}
									middleHeight
									configurationColumns={physicalHeightColumns}
									data={playerData?.playersGrowth?.filter(
										(elem) => elem.height,
									)}
									defaultSortedField="date"
									defaultSortedOrder="descend"
									showModes
									chartData={{
										type: "line",
										title: "Altezza",
										value: "height",
										label: "Altezza",
									}}
									actions={[
										{
											icon: <AddIcon />,
											onClick: () => setOpenPhysicHeightInfo(true),
										},
									]}
									rowActions
									handleEdit={(item) => {
										setInitialData(Object.assign({}, item));
										setOpenPhysicHeightInfo(true);
									}}
									handleDelete={(item) => {
										deletePhysicInfo(item.id);
									}}
								/>
							</div>
							<div className="col-12 col-lg-6 padding-small">
								<CardsTable
									title="Peso"
									fullHeight={false}
									middleHeight
									configurationColumns={physicalWeightColumns}
									data={playerData?.playersGrowth?.filter(
										(elem) => elem.weight,
									)}
									defaultSortedField="date"
									defaultSortedOrder="descend"
									showModes
									chartData={{
										type: "bar",
										title: "Peso",
										value: "weight",
										label: "Peso",
									}}
									actions={[
										{
											icon: <AddIcon />,
											onClick: () => setOpenPhysicInfoWeight(true),
										},
									]}
									rowActions
									handleEdit={(item) => {
										setInitialData(Object.assign({}, item));
										setOpenPhysicInfoWeight(true);
									}}
									handleDelete={(item) => {
										deletePhysicInfo(item.id);
									}}
								/>
							</div>
						</div>
						{openPhysicHeightInfo ? (
							<HandlePhysicHeightInfo
								open={openPhysicHeightInfo}
								confirm={(data, id) =>
									handlePhysicInfo({...data, measurementType: 0}, id)
								}
								close={handleClose}
								initialData={initialData}
							/>
						) : null}
						{openPhysicInfoWeight ? (
							<HandlePhysicWeightInfo
								open={openPhysicInfoWeight}
								confirm={(data, id) =>
									handlePhysicInfo({...data, measurementType: 0}, id)
								}
								close={handleClose}
								initialData={initialData}
							/>
						) : null}
					</div>
				);
			},
		},
		{
			key: "valuations",
			label: t("valuations"),
			render: () => {
				if (currentTab !== "valuations") return <></>;

				const showValuations =
					!playerData?.goalkeeper || valSection === "valuations";
				const showMatchReports =
					playerData?.goalkeeper && valSection === "match_reports";

				return (
					<div className="row no-margin g-2">
						{/* Toggle — only for goalkeepers */}
						{playerData?.goalkeeper && (
							<div className="col-12 px-1">
								<ChoiceChips
									options={[
										{value: "valuations", label: "Valutazioni"},
										{value: "match_reports", label: "Report portiere"},
									]}
									value={valSection}
									onChange={setValSection}
								/>
							</div>
						)}

						{/* Metric bar */}
						{showValuations && (
							<div className="col-12 px-1">
								<div className="vt-metric-bar">
									<div className="vt-metric-bar__item vt-metric-bar__item--accent">
										<span className="vt-metric-bar__label">
											Rating giocatore
										</span>
										<span className="vt-metric-bar__value">
											{playerRating !== null
												? Number(playerRating.toFixed(2))
												: "—"}
										</span>
									</div>
								</div>
							</div>
						)}
						{showMatchReports && (
							<div className="col-12 px-1">
								<div className="vt-metric-bar">
									<div className="vt-metric-bar__item vt-metric-bar__item--accent">
										<span className="vt-metric-bar__label">Rating gara</span>
										<span className="vt-metric-bar__value">
											{matchReportsStats.average !== null
												? Number(matchReportsStats.average.toFixed(2))
												: "—"}
										</span>
									</div>
									<div className="vt-metric-bar__item">
										<span className="vt-metric-bar__label">
											Partite con report
										</span>
										<span className="vt-metric-bar__value">
											{matchReportsStats.count || 0}
										</span>
									</div>
								</div>
							</div>
						)}

						{/* Trend chart — GK match reports section */}
						{showMatchReports && (
							<div className="col-sm-12 padding-small">
								<VLFCard
									title="Andamento report gara"
									openable={false}
									content={
										<div className="row no-margin">
											<div className="col-12">
												<ChartContainer
													type="line"
													chartItems={[
														{
															value: "rating",
															label: "Rating gara",
															color: COLORS.good,
														},
													]}
													data={matchReportsStats.trend}
													dataKey="date"
												/>
											</div>
										</div>
									}
								/>
							</div>
						)}

						{/* Content */}
						{showValuations && <ValuationsPage playerId={id} />}
						{showMatchReports && <MatchReportsPage playerId={id} />}
					</div>
				);
			},
		},
		{
			key: "medical",
			label: t("medical_passport"),
			hidden: !canRead(user, AREAS.AREA_MEDICA),
			render: () => {
				return (
					<MedicalRecordPage
						onlyDetails
						playerId={id}
						handleNotification={handleNotification}
					/>
				);
			},
		},
	];

	const activeContract =
		playerContracts
			?.filter((c) => c.isActive || c.status === "active")
			?.sort((a, b) => new Date(b.endDate) - new Date(a.endDate))?.[0] ||
		playerContracts?.sort(
			(a, b) => new Date(b.endDate) - new Date(a.endDate),
		)?.[0];

	const contractMonthsLeft = activeContract?.endDate
		? moment(activeContract.endDate).diff(moment(), "months")
		: null;

	const contractColorClass =
		contractMonthsLeft === null
			? "no-contract"
			: contractMonthsLeft < 0
				? "expired"
				: contractMonthsLeft <= 12
					? "critical"
					: contractMonthsLeft <= 24
						? "warning"
						: "safe";

	const contractLabel = activeContract?.endDate
		? moment(activeContract.endDate).format("MM/YYYY")
		: "Nessun contratto";

	const playerAge = playerData?.dateOfBirth
		? moment().diff(moment(playerData.dateOfBirth), "years")
		: null;

	const playerRole = playerData?.goalkeeper
		? "Portiere"
		: playerData?.primaryRole || null;

	return (
		<Page className="page-container player-profile-page">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={
					playerData
						? (playerData.firstName || "") + " " + (playerData.lastName || "")
						: ""
				}
				actions={<MainActions actions={mainActions} />}
			/>
			{playerData?.firstName && (
				<PlayerHero
					player={playerData}
					status={isInjured ? "critical" : undefined}
					statusLabel={isInjured ? "Infortunato" : undefined}
					chips={[
						playerRole && {id: "role", label: playerRole, tone: "primary"},
						playerAge !== null && {id: "age", label: playerAge + " anni"},
						playerData?.playerCategoryName && {
							id: "category",
							label: playerData.playerCategoryName,
						},
						playerData?.nationality && {
							id: "nationality",
							label: playerData.nationality,
						},
						statusLabel && {
							id: "status",
							label: statusLabel,
							tone:
								playerData?.status === "Interno" ||
								(playerData?.isInternal && !playerData?.status)
									? "good"
									: "warning",
						},
						isInjured && {
							id: "injury",
							label: (
								<>
									<LocalHospitalIcon sx={{fontSize: 13}} /> Infortunato
								</>
							),
							tone: "critical",
						},
						showCertificates &&
							["expired", "expiring"].includes(certificate.status) && {
								id: "certificate",
								label:
									"Certificato: " + certificateNote(certificate).toLowerCase(),
								tone: CERTIFICATE_STATUS[certificate.status].tone,
							},
						{
							id: "contract",
							label:
								contractMonthsLeft === null
									? "Nessun contratto"
									: "Contratto " +
										contractLabel +
										(contractMonthsLeft < 0
											? " · scaduto"
											: " · " + contractMonthsLeft + " mesi"),
							tone: CONTRACT_TONES[contractColorClass],
						},
					].filter(Boolean)}
					scores={[
						{id: "rating", label: "Valutazione", value: playerRating},
						...(playerData?.goalkeeper || matchReportsStats.average !== null
							? [
									{
										id: "match",
										label: "Voto gara",
										value: matchReportsStats.average,
									},
								]
							: []),
					]}
				/>
			)}
			<Tabs
				freeHeight
				tabs={tabs}
				currentTab={currentTab}
				setCurrentTab={(tab) => dispatch(setPlayerTab(tab))}
			/>
			{openCareerDetail ? (
				<HandleCareerModal
					open={openCareerDetail}
					confirm={handleCareerDetail}
					close={() => {
						setOpenCareerDetail(false);
						setInitialData();
					}}
					initialData={initialData}
					handleNotification={handleNotification}
				/>
			) : null}
		</Page>
	);
}

export default PlayerPage;
